import { useApolloClient } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type MobilityApplicationEntrypointContextDataFragment } from '../../../../api/fragments';
import {
    GetGiftVoucherJourneyDocument,
    type GetGiftVoucherJourneyQuery,
    type GetGiftVoucherJourneyQueryVariables,
} from '../../../../api/queries';
import PortalLoadingElement from '../../../../components/PortalLoadingElement';
import { useThemeComponents } from '../../../../themes';
import getApolloErrors from '../../../../utilities/getApolloErrors';
import { type JourneyStage } from '../../FinderApplicationPublicAccessEntrypoint/types';
import JourneyController from './JourneyController';
import { getStageFromGiftVoucher } from './reducer';

export type GiftCodeJourneyBootstrapProps = {
    endpoint: MobilityApplicationEntrypointContextDataFragment;
    initialToken?: string;
    initialStage: JourneyStage;
};

const JourneyBootStrap = ({ endpoint, initialToken, initialStage }: GiftCodeJourneyBootstrapProps) => {
    const apolloClient = useApolloClient();
    const navigate = useNavigate();
    const { notification } = useThemeComponents();

    const [state, setInitialState] = useState<GetGiftVoucherJourneyQuery['getGiftVoucherJourney'] | null>(null);
    const isInitialized = !!state;

    const isDrafted = !!initialToken;

    useEffect(() => {
        if (isInitialized) {
            // already initialized, we skip it
            return;
        }

        if (initialToken) {
            apolloClient
                .query<GetGiftVoucherJourneyQuery, GetGiftVoucherJourneyQueryVariables>({
                    query: GetGiftVoucherJourneyDocument,
                    fetchPolicy: 'no-cache',
                    variables: {
                        token: initialToken,
                    },
                })
                .then(value => setInitialState(value?.data.getGiftVoucherJourney))
                .catch(error => {
                    const apolloErrors = getApolloErrors(error);

                    if (apolloErrors !== null) {
                        const { $root: rootError } = apolloErrors;

                        if (rootError) {
                            notification.error(rootError);
                        }
                    } else {
                        console.error(error);
                    }
                    navigate('..');
                });
        }
    }, [apolloClient, initialToken, isDrafted, isInitialized, navigate, notification]);

    if (isDrafted && !state) {
        return <PortalLoadingElement />;
    }

    return (
        <JourneyController
            endpoint={endpoint}
            initialGiftVoucher={isDrafted ? state?.giftVoucher : null}
            initialStage={state?.giftVoucher ? getStageFromGiftVoucher(state.giftVoucher) : initialStage}
            initialToken={state?.token}
        />
    );
};

export default JourneyBootStrap;
